import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Sun, Moon, Heart, Sparkles, Activity, Shield, Clock, Video, Layers, Package, User, Eye, FileText, HelpCircle, ChevronRight, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface PricingPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-23415">
      {children}
    </motion.section>;
}
export function PricingPage({
  onNavigate
}: PricingPageProps) {
  const servicesRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBuyPackage = (pkg: {
    title: string;
    desc: string;
    price: number;
    oldPrice?: number;
  }) => {
    addItem({
      id: `pkg-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      oldPrice: pkg.oldPrice,
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-23416">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 pb-14 lg:pb-16" data-id="element-23417">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" data-id="element-23418" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" data-id="element-23419" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[length:40px_40px] pointer-events-none" data-id="element-23420" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23421">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Цены'
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-23422" />

          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl mx-auto text-center relative" data-id="element-23423">
            <motion.h1 variants={staggerItem} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight" data-id="element-23424">
              Выберите астрологический анализ
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base sm:text-lg text-slate-300 mb-3 leading-relaxed max-w-2xl mx-auto" data-id="element-23425">
              Получите персональный астрологический разбор личности, отношений и
              жизненных периодов.
            </motion.p>
            <motion.p variants={staggerItem} className="text-sm text-slate-400 mb-7 max-w-xl mx-auto bg-white/5 py-2 px-4 rounded-full inline-block backdrop-blur-sm border border-white/10" data-id="element-23426">
              Каждая услуга включает бесплатный Beta-результат и полный
              Premium-отчёт.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col items-center" data-id="element-23427">
              <Button size="lg" onClick={() => onNavigate('free-chart')} className="bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30 w-full sm:w-auto" data-id="element-23428">
                Построить натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-23429" />
              </Button>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 text-xs sm:text-sm text-slate-400" data-id="element-23430">
                <div className="flex items-center" data-id="element-23431">
                  <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" data-id="element-23432" />
                  <span data-id="element-23433">10 000+ отчётов</span>
                </div>
                <div className="flex items-center" data-id="element-23434">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-400" data-id="element-23435" />
                  <span data-id="element-23436">Мгновенный результат</span>
                </div>
                <div className="flex items-center" data-id="element-23437">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-emerald-400" data-id="element-23438" />
                  <span data-id="element-23439">Безопасная оплата</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* 2. КАК РАБОТАЕТ ПЛАТФОРМА */}
      <AnimatedSection className="py-12 bg-white" data-id="element-23440">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23441">
          <div className="text-center mb-10" data-id="element-23442">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-23443">
              Как работает платформа
            </h2>
            <p className="text-sm text-slate-600" data-id="element-23444">
              Простой путь от ввода данных до получения полного персонального
              разбора
            </p>
          </div>

          <div className="relative" data-id="element-23445">
            <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 border-t-2 border-dashed border-slate-200" data-id="element-23446" />
            <div className="md:hidden absolute top-10 bottom-10 left-[1.75rem] w-0.5 border-l-2 border-dashed border-slate-200" data-id="element-23447" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" data-id="element-23448">
              {[{
              icon: <User className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-23449" />,
              num: 1,
              title: 'Введите данные рождения',
              desc: 'Укажите дату, время и место рождения для точного расчёта.'
            }, {
              icon: <Eye className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-23450" />,
              num: 2,
              title: 'Получите бесплатный Beta-результат',
              desc: 'Мгновенный краткий анализ основных показателей.'
            }, {
              icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-23451" />,
              num: 3,
              title: 'Разблокируйте полный Premium-отчёт',
              desc: 'Подробный персональный разбор с рекомендациями.'
            }].map(step => <div key={step.num} className="relative flex md:flex-col items-start md:items-center" data-id="element-23452">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-2xl md:rounded-full flex items-center justify-center mb-0 md:mb-4 border border-slate-100 shadow-sm relative z-10 flex-shrink-0 mr-4 md:mr-0" data-id="element-23453">
                    <div className="absolute -top-1.5 -right-1.5 md:top-0 md:right-0 w-5 h-5 md:w-6 md:h-6 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center md:translate-x-1/4 md:-translate-y-1/4 shadow-sm" data-id="element-23454">
                      {step.num}
                    </div>
                    {step.icon}
                  </div>
                  <div className="md:text-center pt-1 md:pt-0" data-id="element-23455">
                    <h3 className="text-sm md:text-base font-bold text-slate-900 mb-1" data-id="element-23456">
                      {step.title}
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500" data-id="element-23457">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. РАЗОВЫЕ УСЛУГИ */}
      <div ref={servicesRef} className="scroll-mt-4" data-id="element-23458">
        <AnimatedSection className="py-12 bg-slate-50" data-id="element-23459">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23460">
            <div className="text-center mb-10" data-id="element-23461">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-23462">
                Разовые астрологические услуги
              </h2>
              <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-23463">
                Выберите конкретный астрологический анализ и получите
                персональный отчёт.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-23464">
              {/* Натальная карта */}
              <motion.div variants={staggerItem} data-id="element-23465">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-indigo-200 transition-colors" data-id="element-23466">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" data-id="element-23467" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-23468">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center" data-id="element-23469">
                      <Moon className="w-5 h-5" data-id="element-23470" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-23471">
                      Натальная карта
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-23472">
                    Анализ личности, характера, талантов и жизненного
                    потенциала.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-23473">
                    <div className="text-xs text-slate-500" data-id="element-23474">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-23475">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-23476">
                    <div className="flex items-center justify-between mb-2" data-id="element-23477">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-23478">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-23479">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-23480">
                      {['Солнце', 'Луна', 'Асцендент', 'краткий психологический профиль'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-23481">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-23482" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-23483" />
                  <div className="mb-5 flex-grow" data-id="element-23484">
                    <div className="flex items-center justify-between mb-2" data-id="element-23485">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-23486">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-23487">
                        от 20 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-23488">
                      {['полный анализ натальной карты', 'планеты в знаках', 'планеты в домах', 'аспекты', 'таланты и предназначение', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-23489">
                          <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23490" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button fullWidth onClick={() => onNavigate('service-natal')} data-id="element-23491">
                    Построить карту
                  </Button>
                </Card>
              </motion.div>

              {/* Соляр */}
              <motion.div variants={staggerItem} data-id="element-23492">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-amber-200 transition-colors" data-id="element-23493">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" data-id="element-23494" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-23495">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-23496">
                      <Sun className="w-5 h-5" data-id="element-23497" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-23498">Соляр</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-23499">
                    Астрологический прогноз на личный год.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-23500">
                    <div className="text-xs text-slate-500" data-id="element-23501">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-23502">
                      4 400 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-23503">
                    <div className="flex items-center justify-between mb-2" data-id="element-23504">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-23505">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-23506">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-23507">
                      {['главная тема года', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-23508">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-23509" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-23510" />
                  <div className="mb-5 flex-grow" data-id="element-23511">
                    <div className="flex items-center justify-between mb-2" data-id="element-23512">
                      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-23513">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-23514">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-23515">
                      {['полный солярный анализ', 'ключевые сферы года', 'важные периоды', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-23516">
                          <Check className="w-3.5 h-3.5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23517" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-solar')} data-id="element-23518">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Комбо */}
              <motion.div variants={staggerItem} data-id="element-23519">
                <Card padding="md" className="h-full flex flex-col border-violet-300 ring-2 ring-violet-100 shadow-lg shadow-violet-500/10 relative overflow-hidden group" data-id="element-23520">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-23521" />
                  <div className="absolute top-3 right-3 z-10" data-id="element-23522">
                    <Badge variant="pro" className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-id="element-23523">
                      Самый популярный
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-1" data-id="element-23524">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-23525">
                      <Layers className="w-5 h-5" data-id="element-23526" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 pr-20" data-id="element-23527">
                      Комбо: Натал + Соляр
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-23528">
                    Полный анализ личности и прогноз на ближайший год.
                  </p>
                  <div className="bg-violet-50 rounded-lg p-3 mb-4 border border-violet-100" data-id="element-23529">
                    <div className="text-xs text-violet-600 font-medium" data-id="element-23530">
                      Premium отчёт
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-23531">
                      <span className="text-2xl font-bold text-slate-900" data-id="element-23532">
                        6 900 ₽
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-23533">
                        8 300 ₽
                      </span>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-23534">
                    <div className="flex items-center justify-between mb-2" data-id="element-23535">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-23536">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-23537">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-23538">
                      {['краткий профиль личности', 'краткий прогноз года'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-23539">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-23540" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-23541" />
                  <div className="mb-5 flex-grow" data-id="element-23542">
                    <div className="flex items-center justify-between mb-2" data-id="element-23543">
                      <span className="text-xs font-bold text-violet-600 uppercase tracking-wider" data-id="element-23544">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded" data-id="element-23545">
                        от 35 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-23546">
                      {['полный анализ натальной карты', 'полный соляр', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-23547">
                          <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23548" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="primary" fullWidth onClick={() => onNavigate('service-combo')} className="bg-violet-600 hover:bg-violet-700" data-id="element-23549">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Совместимость */}
              <motion.div variants={staggerItem} data-id="element-23550">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-rose-200 transition-colors" data-id="element-23551">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-rose-400" data-id="element-23552" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-23553">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" data-id="element-23554">
                      <Heart className="w-5 h-5" data-id="element-23555" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-23556">
                      Совместимость (Синастрия)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-23557">
                    Анализ совместимости двух людей.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-23558">
                    <div className="text-xs text-slate-500" data-id="element-23559">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-23560">
                      4 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-23561">
                    <div className="flex items-center justify-between mb-2" data-id="element-23562">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-23563">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-23564">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-23565">
                      {['общий уровень совместимости', 'краткий анализ'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-23566">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-23567" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-23568" />
                  <div className="mb-5 flex-grow" data-id="element-23569">
                    <div className="flex items-center justify-between mb-2" data-id="element-23570">
                      <span className="text-xs font-bold text-rose-500 uppercase tracking-wider" data-id="element-23571">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-23572">
                        от 25 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-23573">
                      {['эмоциональная совместимость', 'романтическая динамика', 'конфликтные зоны', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-23574">
                          <Check className="w-3.5 h-3.5 text-rose-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-23575" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-compatibility')} data-id="element-23576">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Транзиты */}
              <motion.div variants={staggerItem} data-id="element-23577">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-teal-200 transition-colors" data-id="element-23578">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500" data-id="element-23579" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-23580">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center" data-id="element-23581">
                      <Activity className="w-5 h-5" data-id="element-23582" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-23583">
                      Прогноз (Транзиты)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-23584">
                    Астрологический прогноз на ближайшие месяцы.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-23585">
                    <div className="text-xs text-slate-500" data-id="element-23586">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-23587">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-23588">
                    <div className="flex items-center justify-between mb-2" data-id="element-23589">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-23590">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-23591">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-23592">
                      {['основной транзит', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-23593">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-23594" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-23595" />
                  <div className="mb-5 flex-grow" data-id="element-23596">
                    <div className="flex items-center justify-between mb-2" data-id="element-23597">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider" data-id="element-23598">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded" data-id="element-23599">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-23600">
                      {['полный анализ транзитов', 'ключевые даты', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-23601">
                          <Check className="w-3.5 h-3.5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23602" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-forecast')} data-id="element-23603">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Консультация */}
              <motion.div variants={staggerItem} data-id="element-23604">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-sky-200 transition-colors" data-id="element-23605">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" data-id="element-23606" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-23607">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center" data-id="element-23608">
                      <Video className="w-5 h-5" data-id="element-23609" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-23610">
                      Консультация с астрологом
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-23611">
                    Персональная консультация с профессиональным астрологом.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-23612">
                    <div className="text-xs text-slate-500" data-id="element-23613">Личная встреча</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-23614">
                      от 2 990 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-23615">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-23616">
                      Beta:
                    </span>
                    <p className="text-xs text-slate-400 italic mt-1" data-id="element-23617">
                      Для консультаций Beta отсутствует
                    </p>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-23618" />
                  <div className="mb-5 flex-grow" data-id="element-23619">
                    <span className="text-xs font-bold text-sky-600 uppercase tracking-wider" data-id="element-23620">
                      Формат:
                    </span>
                    <ul className="space-y-1 mt-2" data-id="element-23621">
                      {['онлайн-встреча по видеосвязи', 'персональный разбор карты', 'ответы на ваши вопросы'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-23622">
                          <Check className="w-3.5 h-3.5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-23623" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-consultation')} data-id="element-23624">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>

      {/* 5. TRUST / GUARANTEE */}
      <section className="py-8 bg-white border-y border-slate-100" data-id="element-23625">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23626">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-23627">
            {[{
            icon: <Clock className="w-5 h-5 text-indigo-600" data-id="element-23628" />,
            bg: 'bg-indigo-50',
            title: 'Мгновенный результат',
            desc: 'Отчёт готов сразу после оплаты'
          }, {
            icon: <Shield className="w-5 h-5 text-emerald-600" data-id="element-23629" />,
            bg: 'bg-emerald-50',
            title: 'Безопасная оплата',
            desc: 'Надежное шифрование данных'
          }, {
            icon: <FileText className="w-5 h-5 text-amber-600" data-id="element-23630" />,
            bg: 'bg-amber-50',
            title: 'PDF-отчёт навсегда',
            desc: 'Доступен в личном кабинете'
          }, {
            icon: <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-23631" />,
            bg: 'bg-sky-50',
            title: 'Поддержка 24/7',
            desc: 'Поможем с любым вопросом'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-23632">
                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center mb-2`} data-id="element-23633">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-23634">
                  {item.title}
                </h4>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-23635">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. НЕ ЗНАЕТЕ, ЧТО ВЫБРАТЬ? */}
      <AnimatedSection className="py-12 bg-slate-50" data-id="element-23636">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23637">
          <div className="text-center mb-8" data-id="element-23638">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3" data-id="element-23639">
              <HelpCircle className="w-5 h-5" data-id="element-23640" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-23641">
              Не знаете, что выбрать?
            </h2>
            <p className="text-sm text-slate-600" data-id="element-23642">
              Выберите вашу главную цель, и мы подскажем подходящую услугу
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-23643">
            {[{
            goal: 'Хочу понять себя',
            service: 'Натальная карта',
            page: 'service-natal',
            icon: <Moon className="w-4 h-4" data-id="element-23644" />,
            color: 'text-indigo-600',
            borderHover: 'hover:border-indigo-300'
          }, {
            goal: 'Хочу прогноз на год',
            service: 'Соляр',
            page: 'service-solar',
            icon: <Sun className="w-4 h-4" data-id="element-23645" />,
            color: 'text-amber-600',
            borderHover: 'hover:border-amber-300'
          }, {
            goal: 'Хочу личность и прогноз',
            service: 'Комбо: Натал + Соляр',
            page: 'service-combo',
            icon: <Layers className="w-4 h-4" data-id="element-23646" />,
            color: 'text-violet-600',
            borderHover: 'hover:border-violet-300'
          }, {
            goal: 'Хочу понять отношения',
            service: 'Совместимость',
            page: 'service-compatibility',
            icon: <Heart className="w-4 h-4" data-id="element-23647" />,
            color: 'text-rose-600',
            borderHover: 'hover:border-rose-300'
          }, {
            goal: 'Хочу прогноз на ближайшее время',
            service: 'Транзиты',
            page: 'service-forecast',
            icon: <Activity className="w-4 h-4" data-id="element-23648" />,
            color: 'text-teal-600',
            borderHover: 'hover:border-teal-300'
          }, {
            goal: 'Хочу личный разбор',
            service: 'Консультация',
            page: 'service-consultation',
            icon: <Video className="w-4 h-4" data-id="element-23649" />,
            color: 'text-sky-600',
            borderHover: 'hover:border-sky-300'
          }].map((item, i) => <button key={i} onClick={() => onNavigate(item.page)} className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 text-left transition-all group hover:shadow-sm ${item.borderHover}`} data-id="element-23650">
                <div className={`w-9 h-9 rounded-lg bg-slate-50 ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-23651">
                  {item.icon}
                </div>
                <div className="flex-grow min-w-0" data-id="element-23652">
                  <div className="text-sm font-bold text-slate-900" data-id="element-23653">
                    {item.goal}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-23654">→ {item.service}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 transition-colors" data-id="element-23655" />
              </button>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 7. FAQ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-23656">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23657">
          <div className="text-center mb-8" data-id="element-23658">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-23659">
              Часто задаваемые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Что такое Beta-результат?',
          content: 'Beta — это бесплатный предварительный анализ, который вы получаете сразу после ввода данных рождения. Он показывает основные астрологические показатели и позволяет познакомиться с форматом анализа перед покупкой полного отчёта.'
        }, {
          id: '2',
          title: 'Чем Premium отличается от Beta?',
          content: 'Premium — это полный подробный отчёт с разбором всех элементов карты, прогнозами и практическими рекомендациями. Объём Premium-отчёта значительно больше и включает глубокий персональный анализ.'
        }, {
          id: '3',
          title: 'Можно ли использовать пакет для разных услуг?',
          content: 'Да! Каждый отчёт из пакета можно использовать для любой Premium-услуги: натальная карта, соляр, совместимость или прогноз (транзиты). Вы сами выбираете, на что потратить каждый отчёт.'
        }, {
          id: '4',
          title: 'В каком формате приходит отчёт?',
          content: 'Все отчёты предоставляются в формате PDF. Вы можете скачать их на странице результата сразу после оплаты, а также получить на email. Отчёты доступны в вашем личном кабинете бессрочно.'
        }, {
          id: '5',
          title: 'Сколько времени занимает расчёт?',
          content: 'Автоматические отчёты генерируются мгновенно — сразу после оплаты. Консультация с астрологом назначается на удобное для вас время.'
        }]} data-id="element-23660" />
        </div>
      </AnimatedSection>

      {/* 8. ФИНАЛЬНЫЙ CTA */}
      <AnimatedSection className="py-16 bg-slate-900 relative overflow-hidden" data-id="element-23661">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e520,#8b5cf620)]" data-id="element-23662" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-23663">
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-block mb-5" data-id="element-23664">
            <Star className="w-8 h-8 text-indigo-400" data-id="element-23665" />
          </motion.div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-23666">
            Начните исследовать свою натальную карту
          </h2>
          <p className="text-base text-indigo-100/80 mb-8 max-w-xl mx-auto" data-id="element-23667">
            Введите данные рождения и получите персональный астрологический
            анализ.
          </p>
          <Button size="lg" variant="outline" onClick={() => onNavigate('free-chart')} className="shadow-xl shadow-indigo-500/25 !bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50" data-id="element-23668">
            Построить карту
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-23669" />
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-indigo-200/60" data-id="element-23670">
            <span className="flex items-center" data-id="element-23671">
              <Shield className="w-4 h-4 mr-1.5 text-emerald-400/80" data-id="element-23672" />{' '}
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-23673">
              <Clock className="w-4 h-4 mr-1.5 text-indigo-300/80" data-id="element-23674" /> Мгновенный
              результат
            </span>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}